/* ═══════════════════════════════════════════════════════════════════════════
   FF Shipping Info — Cart-aware shipping rate + free-shipping nudge
   + progress bar (gradient on track, animation classes)
   ═══════════════════════════════════════════════════════════════════════════ */

if (!customElements.get('ff-shipping-info')) {
  class FFShippingInfo extends HTMLElement {
    connectedCallback() {
      this.currency = this.dataset.currency || '$';

      /* ── Rate line config ─────────────────────────────────────── */
      this.firstPrice = this.dataset.firstPrice || '';
      this.additionalPrice = this.dataset.additionalPrice || '';
      this.productId = this.dataset.productId || '';
      this.freeLabel = this.dataset.freeLabel || 'FREE';

      const rawKw = (this.dataset.tierKeywords || '').toLowerCase();
      this.tierKeywords = rawKw
        ? rawKw.split(',').map((k) => k.trim()).filter(Boolean)
        : [];

      /* ── Nudge config ─────────────────────────────────────────── */
      this.threshold = parseFloat(this.dataset.threshold) || 0;
      this.emptyCartMsg = this.dataset.emptyCartMsg || 'Free shipping on orders over $75!';
      this.nudgeTemplate = this.dataset.nudgeTemplate || 'Only {amount} more for free shipping!';
      this.qualifiesMsg = this.dataset.qualifiesMsg || 'Add to cart to unlock free shipping!';
      this.qualifiedMsg = this.dataset.qualifiedMsg || 'Free shipping unlocked';

      /* ── Progress bar config ─────────────────────────────────── */
      this.hideBarEmpty = this.dataset.hideBarEmpty === 'true';
      this.barFillColor = this.dataset.barFillColor || '#16a34a';
      this.barCompleteFillColor = this.dataset.barCompleteFillColor || '#16a34a';
      this.barAnimate = this.dataset.barAnimate === 'true';

      /* ── Parse variant price map ─────────────────────────────── */
      this.variantPrices = {};
      const dataEl = this.querySelector('[data-variant-prices]');
      if (dataEl) {
        try {
          this.variantPrices = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.warn('ff-shipping-info: Could not parse variant prices', e);
        }
      }

      this.cartTotal = 0;
      this.cartHasTierMatch = false;

      /* ── Listen for variant changes ──────────────────────────── */
      const section = this.closest('.product');
      if (section) {
        section.addEventListener('change', (e) => {
          if (e.target.closest('variant-selects, variant-radios')) {
            setTimeout(() => this.render(), 60);
          }
        });
      }

      /* ── Intercept fetch to detect cart changes ──────────────── */
      this._hookFetch();

      /* ── Initial cart fetch + render ─────────────────────────── */
      this.fetchCartAndRender();
    }

    _hookFetch() {
      if (!window.__ffShippingInstances) {
        window.__ffShippingInstances = new Set();
      }
      window.__ffShippingInstances.add(this);

      if (window.__ffShippingHooked) return;
      window.__ffShippingHooked = true;

      const origFetch = window.fetch;

      window.fetch = function (...args) {
        const result = origFetch.apply(this, args);
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

        if (/\/cart\/(add|change|update|clear)/.test(url)) {
          result
            .then((res) => res.clone().json())
            .then(() => {
              setTimeout(() => {
                if (window.__ffShippingInstances) {
                  window.__ffShippingInstances.forEach((inst) => {
                    inst.fetchCartAndRender();
                  });
                }
              }, 300);
            })
            .catch(() => {});
        }
        return result;
      };
    }

    disconnectedCallback() {
      if (window.__ffShippingInstances) {
        window.__ffShippingInstances.delete(this);
      }
    }

    async fetchCartAndRender() {
      try {
        const res = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
        });
        const cart = await res.json();
        this.cartTotal = cart.total_price || 0;

        this.cartHasTierMatch = false;
        if (this.tierKeywords.length && cart.items) {
          for (const item of cart.items) {
            const type = (item.product_type || '').toLowerCase();
            const title = (item.product_title || '').toLowerCase();
            for (const kw of this.tierKeywords) {
              if (type.includes(kw) || title.includes(kw)) {
                this.cartHasTierMatch = true;
                break;
              }
            }
            if (this.cartHasTierMatch) break;
          }
        }
      } catch (e) {
        console.warn('ff-shipping-info: Could not fetch cart', e);
        this.cartTotal = 0;
        this.cartHasTierMatch = false;
      }
      this.render();
    }

    getActivePrice() {
      const params = new URLSearchParams(window.location.search);
      const variantId = params.get('variant');
      if (variantId && this.variantPrices[variantId] !== undefined) {
        return this.variantPrices[variantId];
      }
      const vals = Object.values(this.variantPrices);
      return vals.length ? vals[0] : 0;
    }

    render() {
      const thresholdCents = this.threshold * 100;
      const isFreeShipping = thresholdCents > 0 && this.cartTotal >= thresholdCents;

      this._renderRate(isFreeShipping);
      this._renderNudge(isFreeShipping);
      this._renderProgress(isFreeShipping);
    }

    _renderRate(isFreeShipping) {
      const rateLine = this.querySelector('[data-shipping-rate]');
      if (!rateLine) return;
      const textEl = rateLine.querySelector('.ff-shipping-line__text');
      if (!textEl) return;

      const price = (this.cartHasTierMatch && this.additionalPrice)
        ? this.additionalPrice
        : this.firstPrice;

      if (!price) return;

      if (isFreeShipping) {
        textEl.innerHTML =
          '<span class="ff-si-label">Shipping:</span> ' +
          '<span class="ff-si-free-label">' + this._esc(this.freeLabel) + '</span> ' +
          '<span class="ff-si-price ff-si-price-struck">' + this._esc(this.currency + price) + '</span>';
      } else {
        textEl.innerHTML =
          '<span class="ff-si-label">Shipping:</span> ' +
          '<span class="ff-si-price">' + this._esc(this.currency + price) + '</span>';
      }
    }

    _renderNudge(isFreeShipping) {
      const nudgeLine = this.querySelector('[data-shipping-nudge]');
      if (!nudgeLine) return;
      const textEl = nudgeLine.querySelector('.ff-shipping-line__text');
      if (!textEl || this.threshold <= 0) {
        if (nudgeLine) nudgeLine.hidden = true;
        return;
      }

      nudgeLine.hidden = false;

      const thresholdCents = this.threshold * 100;
      const cartCents = this.cartTotal;
      const productCents = this.getActivePrice();

      if (isFreeShipping) {
        textEl.innerHTML = this.qualifiedMsg;
      } else if (cartCents === 0) {
        textEl.innerHTML = this.emptyCartMsg;
      } else if (cartCents + productCents >= thresholdCents) {
        textEl.innerHTML = this.qualifiesMsg;
      } else {
        const remainingDollars = (thresholdCents - cartCents) / 100;
        const formatted = this.currency + remainingDollars.toFixed(2);
        textEl.innerHTML = this.nudgeTemplate.replace('{amount}', formatted);
      }
    }

    _renderProgress(isFreeShipping) {
      const wrap = this.querySelector('[data-shipping-progress]');
      if (!wrap) return;

      const fill = wrap.querySelector('.ff-si-progress__fill');
      if (!fill) return;

      if (this.threshold <= 0) {
        wrap.hidden = true;
        return;
      }

      if (this.hideBarEmpty && this.cartTotal === 0) {
        wrap.hidden = true;
        return;
      }

      /* Entrance animation on first appearance or hidden → visible */
      const wasHidden = wrap.hidden || !this._barRendered;
      wrap.hidden = false;
      if (wasHidden) {
        wrap.classList.remove('ff-si-progress--entering');
        void wrap.offsetWidth;
        wrap.classList.add('ff-si-progress--entering');
        this._barRendered = true;
      }

      const thresholdCents = this.threshold * 100;
      const pct = Math.min((this.cartTotal / thresholdCents) * 100, 100);

      /* Set fill color + width directly via inline style */
      const fillColor = isFreeShipping ? this.barCompleteFillColor : this.barFillColor;
      fill.style.backgroundColor = fillColor;
      fill.style.width = pct + '%';

      /* Toggle striped animation */
      if (this.barAnimate) {
        wrap.classList.add('ff-si-progress--striped');
      } else {
        wrap.classList.remove('ff-si-progress--striped');
      }

      /* Toggle complete class (stops stripes, triggers celebration flash) */
      const wasComplete = wrap.classList.contains('ff-si-progress--complete');
      if (isFreeShipping) {
        wrap.classList.add('ff-si-progress--complete');

        /* Re-trigger the celebration animation if just became complete */
        if (!wasComplete) {
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      } else {
        wrap.classList.remove('ff-si-progress--complete');
      }
    }

    _esc(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  customElements.define('ff-shipping-info', FFShippingInfo);
}